import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Dayjs } from "@marekl/adminer/datetime";
import { apiDelete, apiGet, apiPatch, apiPost, apiPut, ApiError } from "../api";
import type { ID } from "../id";

/**
 * Competitions (Modul 4 — správa soutěží).
 *
 * A competition pins its season and rule set; the entry phase
 * („Přihlašování") is derived server-side from the status, the uzávěrka
 * and capacity, never stored. `date` / `date_to` are date-only strings
 * per the API convention; `entry_deadline` is a full timestamp and
 * arrives as Dayjs.
 */

export type CompetitionType = "mcr_qualification" | "mcr_final" | "cup";
export type CompetitionStatus =
  | "draft"
  | "announced"
  | "published"
  | "running"
  | "finished"
  | "archived";
export type EntryPhase = "not_open" | "open" | "closed_deadline" | "closed_capacity";

/**
 * The rung of the CLUB's taper: how far a team may still change a
 * přihláška it already holds.
 *
 * Machine keys from internal/service/competition/phase.go. Derived at
 * read time and actor-blind, exactly like entry_phase — a pořadatel
 * reads "locked" on a competition that is wide open to them, so only
 * club surfaces may render it.
 */
export type EntryChangeWindow = "open" | "no_new" | "roster_only" | "locked";

export interface Competition {
  id: ID;
  season_id: ID;
  rule_set_id: ID;
  rule_set_name?: string;
  series_id?: ID;
  series_name?: string;
  type: CompetitionType;
  status: CompetitionStatus;
  name: string;
  date: string;
  date_to?: string;
  venue: string;
  organizer_name: string;
  contact_name: string;
  contact_phone: string;
  contact_email: string;
  entry_deadline?: Dayjs;
  /** The uzávěrka hudby as stored — absent when the competition has none
   * of its own. What actually gates uploads is effective_music_deadline,
   * which falls back to the entry deadline. */
  music_deadline?: Dayjs;
  effective_music_deadline?: Dayjs;
  /** The two uzávěrky that taper the club's window, as STORED — absent
   * when the competition has none of its own, which is what lets the
   * form say „stejná jako …". The effective_* pair is what every gate
   * and every countdown actually reads; the fallback chains upwards,
   * category to the uzávěrka přihlášek and roster to the category. */
  entry_category_deadline?: Dayjs;
  entry_roster_deadline?: Dayjs;
  effective_entry_category_deadline?: Dayjs;
  effective_entry_roster_deadline?: Dayjs;
  entry_change_window: EntryChangeWindow;
  max_capacity?: number;
  /** Changeover between performances; drives every time on the start
   * list. It lives on the competition because it describes the venue and
   * the crew, not one document about them. */
  start_gap_seconds: number;
  /** Submitted entries only — the same count the derived entry_phase
   * compares against max_capacity, so a full competition reads
   * closed_capacity on the wire and not only at submit time. */
  entry_count: number;
  entry_phase: EntryPhase;
  /** Detail responses only — the list omits it. */
  created_at: Dayjs;
  updated_at: Dayjs;
}

export interface CompetitionSeries {
  id: ID;
  season_id: ID;
  name: string;
  created_at: Dayjs;
  updated_at: Dayjs;
}

export interface CompetitionFilters {
  season_id?: ID;
  type?: CompetitionType;
  status?: CompetitionStatus;
  q?: string;
}

export interface CompetitionPayload {
  season_id: ID;
  rule_set_id: ID;
  series_id?: ID | null;
  type: CompetitionType;
  name: string;
  date: string;
  date_to?: string;
  venue?: string;
  organizer_name?: string;
  contact_name?: string;
  contact_phone?: string;
  contact_email?: string;
  /** ISO timestamp — call .toISOString() on the Dayjs value. */
  entry_deadline?: string | null;
  entry_category_deadline?: string | null;
  entry_roster_deadline?: string | null;
  music_deadline?: string | null;
  max_capacity?: number | null;
  start_gap_seconds?: number;
}

export interface SeriesPayload {
  season_id: ID;
  name: string;
}

function filterParams(filters: CompetitionFilters): string {
  const params = new URLSearchParams();
  if (filters.season_id) params.set("season_id", String(filters.season_id));
  if (filters.type) params.set("type", filters.type);
  if (filters.status) params.set("status", filters.status);
  if (filters.q) params.set("q", filters.q);
  const s = params.toString();
  return s ? `?${s}` : "";
}

/**
 * The competition list, optionally filtered.
 *
 * `enabled` matters for any SEASON-SCOPED caller. The endpoint applies no
 * season filter when `season_id` is absent, so a page that passes
 * `season_id: selectedSeason?.id` fires an UNFILTERED request on its first
 * render — before the seasons query has resolved — and paints every
 * season's competitions under a topbar chip naming the active one. Gate on
 * the season being known. Callers that genuinely want every season
 * (ManageRolesModal) pass no filter and need no gate.
 */
export function useCompetitions(
  filters: CompetitionFilters = {},
  options: { enabled?: boolean } = {},
) {
  return useQuery<Competition[]>({
    queryKey: ["competitions", filters],
    queryFn: async () => {
      const res = await apiGet<{ data: Competition[] | null }>(
        `/competitions${filterParams(filters)}`,
      );
      return res.data ?? [];
    },
    enabled: options.enabled ?? true,
  });
}

export function useCompetition(id: ID | undefined) {
  return useQuery<Competition>({
    queryKey: ["competitions", "detail", id],
    queryFn: () => apiGet<Competition>(`/competitions/${id}`),
    enabled: !!id,
  });
}

export function useCompetitionSeries(seasonId?: ID) {
  return useQuery<CompetitionSeries[]>({
    queryKey: ["competition-series", seasonId ?? "all"],
    queryFn: async () => {
      const suffix = seasonId ? `?season_id=${seasonId}` : "";
      const res = await apiGet<{ data: CompetitionSeries[] | null }>(
        `/competition-series${suffix}`,
      );
      return res.data ?? [];
    },
  });
}

// Every competition write invalidates both list roots — a new series
// shows up in the picker, a status flip re-sorts the list.
function useCompetitionMutation<TData, TVars>(mutationFn: (vars: TVars) => Promise<TData>) {
  const qc = useQueryClient();
  return useMutation<TData, ApiError, TVars>({
    mutationFn,
    // Every caller renders its own: the calendar form and the offer card in
    // an Alert, the lifecycle transitions and the draft delete in the confirm
    // dialog's error slot.
    meta: { suppressErrorToast: true },
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ["competitions"] }),
        qc.invalidateQueries({ queryKey: ["competition-series"] }),
      ]),
  });
}

export function useCreateCompetition() {
  return useCompetitionMutation<Competition, CompetitionPayload>((p) => apiPost("/competitions", p));
}

export function useUpdateCompetition(id: ID) {
  return useCompetitionMutation<Competition, CompetitionPayload>((p) =>
    apiPatch(`/competitions/${id}`, p),
  );
}

export function useDeleteCompetition() {
  return useCompetitionMutation<void, ID>((id) => apiDelete(`/competitions/${id}`));
}

export function useSetCompetitionStatus() {
  return useCompetitionMutation<Competition, { id: ID; status: CompetitionStatus }>((p) =>
    apiPost(`/competitions/${p.id}/status`, { status: p.status }),
  );
}

export function useCreateCompetitionSeries() {
  return useCompetitionMutation<CompetitionSeries, SeriesPayload>((p) =>
    apiPost("/competition-series", p),
  );
}

// ---- competition mode (settings) ----

export interface CompetitionOrganizer {
  assignment_id: ID;
  user_id: ID;
  display_name: string;
  email: string;
}

export function useCompetitionOrganizers(competitionId: ID | undefined) {
  return useQuery<CompetitionOrganizer[]>({
    queryKey: ["competitions", "organizers", competitionId],
    queryFn: async () => {
      const res = await apiGet<{ data: CompetitionOrganizer[] | null }>(
        `/competitions/${competitionId}/organizers`,
      );
      return res.data ?? [];
    },
    enabled: !!competitionId,
  });
}

/**
 * What a competition vypisuje: which disciplines run and which
 * performance-class codes they run in.
 *
 * It belongs to the whole SERIES, not to one event — the rounds of a
 * championship must offer the same thing or their results are not
 * comparable. `scope` says where it lives ("series:12" or
 * "competition:7") and `competitions` how many events share it, which is
 * worth knowing before narrowing a whole championship from one screen.
 *
 * Stored as exclusions so the default — everything the rule set allows —
 * is zero rows; the settings screen shows the inverse as ticked boxes.
 */
export interface CompetitionOffer {
  scope: string;
  excluded_discipline_ids: ID[];
  excluded_class_codes: string[];
  /** Entries are already possible, so the offer may no longer be narrowed. */
  locked: boolean;
  competitions: number;
}

export interface OfferPayload {
  excluded_discipline_ids?: ID[];
  excluded_class_codes?: string[];
}

export function useCompetitionOffer(competitionId: ID | undefined) {
  return useQuery<CompetitionOffer>({
    queryKey: ["competitions", "offer", competitionId],
    enabled: !!competitionId,
    queryFn: () => apiGet<CompetitionOffer>(`/competitions/${competitionId}/offer`),
  });
}

export function useUpdateCompetitionOffer(competitionId: ID) {
  return useCompetitionMutation<CompetitionOffer, OfferPayload>((payload) =>
    apiPut(`/competitions/${competitionId}/offer`, payload),
  );
}
